import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import SimpleEmbedForm from "@/components/posit/SimpleEmbedForm";
import PositDeleted from "@/components/posit/PositDeleted";

const EmbeddedOnly = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [interactions, setInteractions] = useState(0);

  // Fetch the posit data - hook must be called before any conditional returns
  const { data: posit, isLoading, error } = useQuery({
    queryKey: ["embedded-only-posit", id],
    enabled: !!id, // Only run query if id is present
    queryFn: async () => {
      const { data, error } = await supabase
        .from("posits")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        throw new Error("Posit not found");
      }

      if (data.embed_enabled !== true) {
        throw new Error("Posit not available for embedding");
      }

      // Check domain restrictions
      if (data.embed_domains && data.embed_domains.length > 0) {
        const currentDomain = embedConfig.domain;
        const allowedDomains = data.embed_domains;
        
        // Check if wildcard is allowed (allows all domains)
        const hasWildcard = allowedDomains.includes('*');
        
        if (!hasWildcard) {
          const isDomainAllowed = allowedDomains.some((domain: string) => 
            currentDomain === domain || currentDomain.endsWith(`.${domain}`)
          );
          
          if (!isDomainAllowed) {
            throw new Error("Domain not allowed");
          }
        }
      }

      return data;
    },
  });

  // Early return after hooks are called
  if (!id) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', fontFamily: 'system-ui' }}>
        Invalid embed URL
      </div>
    );
  }

  // Always use dark theme
  const validTheme = "dark";

  // Extract embed configuration from URL params
  const embedConfig = {
    theme: "dark" as const,
    showBranding: searchParams.get("branding") !== "false",
    maxWidth: searchParams.get("width") || "100%",
    borderRadius: searchParams.get("radius") || "8px",
    primaryColor: searchParams.get("color") || "#2563eb",
    domain: typeof window !== "undefined" ? window.location.hostname : "localhost",
    placeholder: searchParams.get("placeholder") || "Ask a question..."
  };

  // Track interactions for analytics
  const handleInteraction = (type: 'submit' | 'click') => {
    setInteractions(prev => prev + 1);
    
    // Track the interaction (you can enhance this with proper analytics)
  };

  if (isLoading) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        fontFamily: 'system-ui',
        maxWidth: embedConfig.maxWidth,
        borderRadius: embedConfig.borderRadius,
        border: '1px solid #e2e8f0',
        backgroundColor: '#ffffff'
      }}>
        Loading...
      </div>
    );
  }

  if (error || !posit) {
    return (
      <PositDeleted
        context="embed"
        isAuthenticated={false}
        positId={id}
      />
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: embedConfig.maxWidth,
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#ffffff'
    }}>
      <SimpleEmbedForm 
        posit={posit}
        embedConfig={embedConfig}
        onInteraction={handleInteraction}
      />
    </div>
  );
};

export default EmbeddedOnly; 